import { useState } from "react";
import axios from "axios";
import { GitBranch, GitCompare, Lock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { diffKleur } from "@/lib/diff-kleur";

/**
 * EG3 (CA4, opdracht Frank 1 november 2026): takken maken, verwijderen en vergelijken.
 *
 * Indeling naar GitHub: de lijst Branches met de hoofdtak bovenaan als "standaard", per tak Vergelijk en een prullenbak,
 * en Compare met "basis ... vergelijk", hoeveel vastleggingen elke kant voor ligt, de vastleggingen en de diff. De
 * hoofdtak heeft geen prullenbak. Verwijderen vraagt een tweede klik in de rij zelf (geen venster van de browser).
 */
export default function RepoTakken({ repo, takken, onTakken, onVergelijkKlaar = null, onTakWeg = () => {} }) {
  const { t } = useTaal();
  const hoofd = repo.default_branch || "main";
  const [naam, setNaam] = useState("");
  const [van, setVan] = useState(hoofd);
  const [teVerwijderen, setTeVerwijderen] = useState("");
  const [vergelijking, setVergelijking] = useState(null);
  const [bezig, setBezig] = useState(false);
  // GL225-3b: de al samengevoegde takken in een keer opruimen, na een tweede klik. null = de lijst is dicht.
  const [samen, setSamen] = useState(null);
  const [opruimen, setOpruimen] = useState(false);

  const fout = (e, standaard) => {
    const tekst = e?.response?.data?.detail;
    toast.error(typeof tekst === "string" ? tekst : standaard);
  };

  async function maak(e) {
    e.preventDefault();
    setBezig(true);
    try {
      const { data } = await axios.post(`${API}/repos/${repo.id}/takken`, { naam: naam.trim(), van });
      onTakken(data.takken);
      setNaam("");
      toast.success(t("Tak {naam} gemaakt.", { naam: data.tak }));
    } catch (err) {
      fout(err, t("De tak kon niet worden gemaakt."));
    } finally {
      setBezig(false);
    }
  }

  async function verwijder(tak) {
    if (teVerwijderen !== tak) return setTeVerwijderen(tak);
    setTeVerwijderen("");
    try {
      const { data } = await axios.delete(`${API}/repos/${repo.id}/takken`, { params: { naam: tak } });
      onTakken(data.takken);
      if (vergelijking?.met === tak) setVergelijking(null);
      toast.success(t("Tak {naam} verwijderd.", { naam: tak }));
      onTakWeg(tak);
    } catch (err) {
      fout(err, t("De tak kon niet worden verwijderd."));
    }
  }

  async function toonSamengevoegd() {
    try {
      setSamen((await axios.get(`${API}/repos/${repo.id}/takken/samengevoegd`)).data);
      setOpruimen(false);
    } catch (err) {
      fout(err, t("De samengevoegde takken konden niet worden opgehaald."));
    }
  }

  async function ruimOp() {
    const vrij = (samen || []).filter((s) => !s.in_gebruik).map((s) => s.naam);
    if (!opruimen) return setOpruimen(true);
    setOpruimen(false);
    try {
      const { data } = await axios.post(`${API}/repos/${repo.id}/takken/opruimen`, { takken: vrij });
      onTakken(data.takken);
      setSamen(null);
      toast.success(t("{n} takken opgeruimd.", { n: data.verwijderd.length }));
    } catch (err) {
      fout(err, t("Opruimen lukte niet."));
    }
  }

  async function vergelijk(tak) {
    try {
      const { data } = await axios.get(`${API}/repos/${repo.id}/vergelijk`, { params: { basis: hoofd, met: tak } });
      setVergelijking(data);
      onVergelijkKlaar?.(data);
    } catch (err) {
      fout(err, t("Vergelijken lukte niet."));
    }
  }

  return (
    <div className="space-y-3" data-testid="repo-takken">
      <form onSubmit={maak} className="flex flex-wrap items-center gap-2" data-testid="tak-maken">
        <input value={naam} onChange={(e) => setNaam(e.target.value)} placeholder={t("Naam van de nieuwe tak")}
               aria-label={t("Naam van de nieuwe tak")} className="min-w-0 flex-1 rounded-md border bg-transparent px-2 py-1 text-sm"
               data-testid="tak-naam" />
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          {t("vanaf")}
          <select value={van} onChange={(e) => setVan(e.target.value)} className="rounded-md border bg-transparent px-1 py-1"
                  aria-label={t("Vanaf welke tak")} data-testid="tak-van">
            {takken.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <Button type="submit" size="sm" className="tap-target" disabled={bezig || !naam.trim()} data-testid="tak-maak">
          {t("Tak maken")}
        </Button>
      </form>

      <ul className="divide-y rounded-lg border" data-testid="takken-lijst">
        {takken.map((b) => (
          <li key={b} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`tak-${b}`}>
            <GitBranch className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">{b}</span>
            {b === hoofd ? (
              <>
                <span className="rounded-full border px-2 text-xs text-muted-foreground">{t("standaard")}</span>
                {/* EG4: beschermd, niet uit te zetten; alleen een samengevoegde pull request verandert hem. */}
                {(repo.beschermd || []).includes(b) && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`beschermd-${b}`}
                        title={t("Alleen een samengevoegde pull request verandert deze tak, na jouw bevestiging.")}>
                    <Lock className="h-3 w-3" aria-hidden="true" />{t("beschermd")}
                  </span>
                )}
              </>
            ) : (
              <>
                <Button type="button" size="sm" variant="outline" className="tap-target" onClick={() => vergelijk(b)}
                        data-testid={`vergelijk-${b}`}>
                  <GitCompare className="h-4 w-4 mr-1" />{t("Vergelijk")}
                </Button>
                <Button type="button" size="sm" variant={teVerwijderen === b ? "destructive" : "ghost"} className="tap-target"
                        onClick={() => verwijder(b)} aria-label={t("Verwijder tak {naam}", { naam: b })}
                        data-testid={`verwijder-${b}`}>
                  <Trash2 className="h-4 w-4" />{teVerwijderen === b ? <span className="ml-1">{t("Zeker weten?")}</span> : null}
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>

      <div className="space-y-2" data-testid="takken-opruimen">
        <Button type="button" size="sm" variant="outline" className="tap-target" onClick={toonSamengevoegd}
                data-testid="toon-samengevoegd">
          {t("Samengevoegde takken opruimen")}
        </Button>
        {samen && (
          samen.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="geen-samengevoegd">
              {t("Er zijn geen takken die al helemaal in {hoofd} zitten.", { hoofd })}
            </p>
          ) : (
            <>
              <ul className="divide-y rounded-lg border text-sm" data-testid="samengevoegd-lijst">
                {samen.map((s) => (
                  <li key={s.naam} className="flex items-center gap-2 px-3 py-1.5" data-testid={`samengevoegd-${s.naam}`}>
                    <span className="min-w-0 flex-1 truncate">{s.naam}</span>
                    {s.in_gebruik
                      ? <span className="text-xs text-muted-foreground">{t("blijft: de werkruimte {naam} werkt erop", { naam: s.in_gebruik })}</span>
                      : <code className="text-xs text-muted-foreground">{s.kort}</code>}
                  </li>
                ))}
              </ul>
              {samen.some((s) => !s.in_gebruik) && (
                <Button type="button" size="sm" variant={opruimen ? "destructive" : "outline"} className="tap-target"
                        onClick={ruimOp} data-testid="ruim-op">
                  {opruimen ? t("Zeker weten?")
                    : t("Verwijder {n} samengevoegde takken", { n: samen.filter((s) => !s.in_gebruik).length })}
                </Button>
              )}
            </>
          )
        )}
      </div>

      {vergelijking && (
        <div className="space-y-2" data-testid="vergelijking">
          <p className="text-sm">
            <code>{vergelijking.basis}</code> ... <code>{vergelijking.met}</code>
            {" · "}{t("{voor} voor, {achter} achter", { voor: vergelijking.voor, achter: vergelijking.achter })}
          </p>
          {vergelijking.commits.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="vergelijking-gelijk">
              {t("Deze tak heeft geen vastleggingen die {basis} nog niet heeft.", { basis: vergelijking.basis })}
            </p>
          ) : (
            <>
              <ol className="divide-y rounded-lg border text-sm" data-testid="vergelijking-commits">
                {vergelijking.commits.map((c) => (
                  <li key={c.sha} className="flex gap-2 px-3 py-1.5"><span className="min-w-0 flex-1 break-words">{c.boodschap}</span><code className="text-xs text-muted-foreground">{c.kort}</code></li>
                ))}
              </ol>
              <pre className="overflow-x-auto rounded-lg border text-xs leading-5" data-testid="vergelijking-diff"><code>{vergelijking.diff.split("\n").map((r, i) => (
                <div key={i} className={`whitespace-pre px-3 ${diffKleur(r)}`}>{r || " "}</div>
              ))}</code></pre>
              {vergelijking.afgekapt && <p className="text-xs text-muted-foreground">{t("De diff is ingekort; de eerste 400 regels staan hier.")}</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
